/** @jsxImportSource @emotion/react */
import "./modal.css";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Modal from "react-modal";
import { theme } from "styles/theme";
import Icon from "uis/Icon";
import { TVisit, TVisitor } from "apis/visitors/type";
import { getKorDate, getKorTime } from "utils/date";
import StateTag from "../../table/state-tag";
import VisitorTable from "../../table/visitor-table";
import Select_V2 from "uis/Select/select_v2";
import { deleteRsv, getVisitRecord } from "apis/visitors";
import { encrypt } from "utils/crypto";
import { useEffect, useState } from "react";
import ChangeVisitStatusModal from "../change-visit-status-modal.tsx";
import { changeBulkVisitorState } from "apis/visitors/visitor";
import { deleteVisit } from "apis/visitors/record";
import { useRecoilValue } from "recoil";
import { member } from "recoil/recoilState";

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  record?: TVisit;
  onChange?: () => void;
  isPlannedVisit?: boolean;
};

Modal.setAppElement("#root");

const Heading = styled.p`
  color: ${theme.neutral900};
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeadingContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid ${theme.neutral50};
  padding: 30px 34px 20px 34px;
`;

const SectionLabel = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.neutral800};
  margin-bottom: 20px;
  display: flex;
`;

const ContentContainer = styled.div`
  padding: 30px 40px;
`;

const BasicItem = styled.div`
  height: 56px;
  align-items: center;
  padding: 0 30px;
  display: flex;
  justify-content: space-between;
  border-bottom: 2px solid ${theme.neutral50};
`;

const BasicItemLabel = styled.p`
  color: ${theme.neutral900};
  font-size: 16px;
  font-weight: 600;
`;

const BasicItemValue = styled.p`
  color: ${theme.neutral600};
  font-size: 16px;
  font-weight: 500;
`;

const BasicTable = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f9fbff;
  border-radius: 20px;
`;

const modalStyle = {
  content: {
    left: "auto",
    minWidth: "900px",
    width: "45%",
    right: 0,
    top: 0,
    bottom: 0,
    padding: 0,
  },
  overlay: {
    backgroundColor: "var(--component-color-material-dimmer)",
    zIndex: 1000,
  },
};

export default function VisitDetailModal(props: Props) {
  const { modalIsOpen, closeModal, record, onChange, isPlannedVisit = false } = props;
  const memberInfo = useRecoilValue(member);
  const owner = record?.visitRecordManagers.find((item) => item.isOrganizer)?.manager;

  const [open, setOpen] = useState<boolean>(false);
  const [visit, setVisit] = useState<TVisit>();
  const onDeleteVisit = async () => {
    if (record) {
      const res = await deleteVisit(record?.id);

      if (res) {
        onChange?.();
        closeModal();
      }
    }
  };

  const onEditVisit = () => {
    if (record) window.location.href = `/visitors/register/${encodeURIComponent(encrypt(record?.id))}`;
  };

  useEffect(() => {
    if (modalIsOpen) {
      setVisit(record);
    }
  }, [record, modalIsOpen]);

  return (
    <Modal isOpen={modalIsOpen} onRequestClose={closeModal} closeTimeoutMS={400} style={modalStyle}>
      {visit && (
        <>
          <HeadingContainer>
            <Heading>
              <p>
                {visit.visitors[0]?.name}
                {visit.visitors.length > 1 && `외 ${visit.visitors.length - 1}명`}의 방문
              </p>
              {memberInfo?.id !== owner?.id ||
              visit.visitRecordStatus === "CHECK_OUT" ||
              visit.visitRecordStatus === "MISSED_CHECK_OUT" ||
              visit.visitRecordStatus === "CHECK_IN" ||
              visit.visitRecordStatus === "NOT_VISITED" ? (
                <></>
              ) : (
                <Select_V2
                  RenderInput={<Icon name="circle-menu" width={24} height={24} color={theme.neutral400} />}
                  data={["수정하기", "삭제하기"]}
                  styles={{
                    item: css`
                      font-size: 16px;
                      text-align: center;
                    `,
                  }}
                  onChange={(value) => {
                    if (value === "수정하기") onEditVisit();
                    if (value === "삭제하기") onDeleteVisit();
                  }}
                />
              )}
            </Heading>
            <Icon name="close" width={24} height={24} color={theme.neutral900} onClick={closeModal} />
          </HeadingContainer>
          <ContentContainer>
            <SectionLabel>기본 방문 정보</SectionLabel>
            <BasicTable>
              <BasicItem>
                <BasicItemLabel>방문 주최자</BasicItemLabel>
                <BasicItemValue>
                  {owner?.nickname} ({owner?.email})
                </BasicItemValue>
              </BasicItem>
              <BasicItem>
                <BasicItemLabel>방문 담당자</BasicItemLabel>
                <BasicItemValue
                  style={{
                    maxWidth: "85%",
                    whiteSpace: "normal",
                    wordBreak: "break-all",
                  }}
                >
                  {visit.visitRecordManagers
                    .filter((item) => !item.isOrganizer)
                    .map((item) => `${item.manager.nickname} (${item.manager.email})`)
                    .join(", ")}
                </BasicItemValue>
              </BasicItem>
              <BasicItem>
                <BasicItemLabel>방문 일자</BasicItemLabel>
                <BasicItemValue>{getKorDate(visit.visitDatetime)}</BasicItemValue>
              </BasicItem>
              <BasicItem>
                <BasicItemLabel>방문 시간</BasicItemLabel>
                <BasicItemValue>{getKorTime(visit.visitDatetime)}</BasicItemValue>
              </BasicItem>
              <BasicItem>
                <BasicItemLabel>방문 상태</BasicItemLabel>
                <BasicItemValue>
                  <StateTag state={visit.visitRecordStatus} />
                </BasicItemValue>
              </BasicItem>
              <BasicItem>
                <BasicItemLabel>방문자 소속</BasicItemLabel>
                <BasicItemValue>{visit.visitorDepartment}</BasicItemValue>
              </BasicItem>
              <BasicItem>
                <BasicItemLabel>방문 목적</BasicItemLabel>
                <BasicItemValue>{visit.visitorPurpose}</BasicItemValue>
              </BasicItem>
            </BasicTable>
          </ContentContainer>
          <ContentContainer>
            <SectionLabel>
              <p
                css={css`
                  margin-right: auto;
                `}
              >
                방문자 정보
              </p>
              {!visit.visitors.every((item) => item.visitorStatus === "CHECK_OUT") && !isPlannedVisit && (
                <p
                  onClick={() => setOpen(true)}
                  css={css`
                    color: ${theme.primary500};
                    display: flex;
                    cursor: pointer;
                  `}
                >
                  <Icon name="check" color={theme.primary500} />
                  일괄 방문 완료
                </p>
              )}
            </SectionLabel>
            <VisitorTable
              isPlannedVisit={isPlannedVisit}
              visitors={visit.visitors}
              onChange={async () => {
                onChange?.();
                const res = await getVisitRecord(visit.id);
                if (res) {
                  setVisit(res);
                }
              }}
              visitRecordId={visit.id}
            />
          </ContentContainer>
          <ChangeVisitStatusModal
            modalIsOpen={open}
            closeModal={() => setOpen(false)}
            onHandleConfirm={async () => {
              const res = await changeBulkVisitorState(visit.id);
              if (res.success) {
                setOpen(false);
                onChange?.();
                const new_visit = await getVisitRecord(visit.id);
                if (new_visit) {
                  setVisit(new_visit);
                }
              }
            }}
            title="모든 방문자를 방문 완료 처리하시겠습니까?"
            description={
              <p>
                태블릿에서 체크인/체크아웃을 하지 않은 경우
                <br />
                미방문/체크아웃 누락 상태일 수 있습니다.
                <br />
                <p
                  css={css`
                    color: ${theme.neutral400};
                    font-size: 15px;
                    margin-top: 12px;
                  `}
                >
                  * 개인정보 미동의자의 방문 상태는 변경되지 않습니다.
                </p>
              </p>
            }
          />
        </>
      )}
    </Modal>
  );
}
